<template>
  <div class="business-settings d-flex" style="height: calc(100vh - 64px); overflow: hidden;">
    <div
      class="business-settings__col business-settings__col--tree"
      :style="{ width: treeWidth + 'px', flexShrink: 0, overflowY: 'auto', borderRight: '1px solid #F3F4F6' }"
    >
      <entity-tree ref="entityTree" @select="onSelectEntity" @entity-added="onSaved" />
    </div>
    <div class="business-settings__resize-handle" @mousedown="startResize($event)"></div>
    <div class="business-settings__col business-settings__col--categories" style="flex: 0 0 85px; width: 85px;">
      <div v-if="!selection" class="business-settings__col--categories-empty"></div>
      <category-list
        v-else
        :entity-type="selection && selection.type"
        :warehouse-module-active="warehouseModuleActive"
        :selection-key="selection && selection.entity && selection.entity.id"
        @select-category="onSelectCategory"
      />
    </div>
    <div class="business-settings__col business-settings__col--form" style="flex: 1 1 auto; display: flex; flex-direction: column; overflow: hidden;">
      <div v-if="!selection" class="business-settings__empty-state">Odaberite firmu, prodajno mjesto ili blagajnu s lijeve strane.</div>
      <template v-else>
        <div class="business-settings__form-heading">
          <div class="business-settings__form-eyebrow">{{ formEyebrow }}</div>
          <div class="business-settings__form-title">{{ selection.entity.name }}</div>
        </div>
        <div class="business-settings__form-body">
          <component
            v-if="selectedCategory"
            :is="selectedCategory.component"
            v-bind="formProps"
            @saved="onSaved"
          />
        </div>
      </template>
    </div>
    <v-snackbar v-model="snackbar" timeout="3000" color="#00BCD4" class="business-settings__snackbar">Spremljeno.</v-snackbar>
  </div>
</template>

<script>
import EntityTree from './EntityTree'
import CategoryList from './CategoryList'
import EditCompanyInline from '@/modules/companies/components/EditCompanyInline'
import EditReceiptSettingsInline from '@/modules/companies/components/EditReceiptSettingsInline'
import CertificateUploadFormInline from '@/modules/companies/components/CertificateUploadFormInline'
import EditPointOfSaleInline from '@/modules/company/components/EditPointOfSaleInline'
import EditPointOfSaleReceiptSettingsInline from '@/modules/company/components/EditPointOfSaleReceiptSettingsInline'
import EditPointOfSaleTaxesInline from '@/modules/company/components/EditPointOfSaleTaxesInline'
import PrinterConfigurationInline from '@/modules/point-of-sale/components/printer-configuration/PrinterConfigurationInline'
import OrganizationalUnitConfigurationInline from '@/modules/point-of-sale/components/organizational-unit-configuration/OrganizationalUnitConfigurationInline'
import EditCashRegisterInline from '@/modules/point-of-sale/components/EditCashRegisterInline'
import EditCashRegisterReceiptSettingsInline from '@/modules/point-of-sale/components/EditCashRegisterReceiptSettingsInline'

const MIN_COL_WIDTH = 200
const DEFAULT_TREE_WIDTH = 350
const NAME_EDIT_COMPONENTS = ['EditCompanyInline', 'EditPointOfSaleInline', 'EditCashRegisterInline']

export default {
  name: 'BusinessSettings',
  components: {
    EntityTree,
    CategoryList,
    EditCompanyInline,
    EditReceiptSettingsInline,
    CertificateUploadFormInline,
    EditPointOfSaleInline,
    EditPointOfSaleReceiptSettingsInline,
    EditPointOfSaleTaxesInline,
    PrinterConfigurationInline,
    OrganizationalUnitConfigurationInline,
    EditCashRegisterInline,
    EditCashRegisterReceiptSettingsInline
  },
  data () {
    return {
      treeWidth: DEFAULT_TREE_WIDTH,
      resizingTree: false,
      resizeStartX: 0,
      resizeStartWidth: 0,
      selection: null,
      selectedCategory: null,
      warehouseModuleActive: false,
      snackbar: false
    }
  },
  computed: {
    formProps () {
      if (!this.selection) return {}
      if (this.selection.type === 'company') {
        return { company: this.selection.entity }
      }
      if (this.selection.type === 'location') {
        return {
          location: this.selection.entity,
          pointOfSale: this.selection.entity,
          company: this.selection.company
        }
      }
      return {
        cashRegister: this.selection.entity,
        company: this.selection.company,
        location: this.selection.location
      }
    },
    formEyebrow () {
      if (!this.selection) return ''
      if (this.selection.type === 'company') {
        return 'Postavke firme'
      }
      if (this.selection.type === 'location') {
        return 'Postavke lokacije'
      }
      return 'Postavke blagajne'
    }
  },
  beforeDestroy () {
    window.removeEventListener('mousemove', this.onResize)
    window.removeEventListener('mouseup', this.stopResize)
  },
  methods: {
    startResize (event) {
      this.resizingTree = true
      this.resizeStartX = event.clientX
      this.resizeStartWidth = this.treeWidth
      window.addEventListener('mousemove', this.onResize)
      window.addEventListener('mouseup', this.stopResize)
      event.preventDefault()
    },
    onResize (event) {
      if (!this.resizingTree) return
      const delta = event.clientX - this.resizeStartX
      this.treeWidth = Math.max(MIN_COL_WIDTH, this.resizeStartWidth + delta)
    },
    stopResize () {
      this.resizingTree = false
      window.removeEventListener('mousemove', this.onResize)
      window.removeEventListener('mouseup', this.stopResize)
    },
    onSelectEntity (payload) {
      const isSameEntity = this.selection &&
        this.selection.type === payload.type &&
        this.selection.entity.id === payload.entity.id
      this.selection = payload
      if (!isSameEntity) {
        this.selectedCategory = null
      }
      if (payload.type === 'location' &&
        payload.company &&
        payload.company.warehouse &&
        payload.company.warehouse === 'module') {
        this.warehouseModuleActive = true
      } else {
        this.warehouseModuleActive = false
      }
    },
    onSelectCategory (category) {
      this.selectedCategory = category
    },
    onSaved () {
      this.snackbar = true
      if (this.selection && this.selectedCategory && NAME_EDIT_COMPONENTS.includes(this.selectedCategory.component)) {
        this.$refs.entityTree.markPendingUpdate(this.selection.type, this.selection.entity.id)
      }
    }
  }
}
</script>

<style scoped>
.business-settings__col--categories {
  width: 85px;
  flex: 0 0 85px;
  overflow-y: auto;
  border-right: 1px solid #F3F4F6;
}
.business-settings__resize-handle {
  width: 4px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
}
.business-settings__resize-handle:hover {
  background: #bdbdbd;
}
.business-settings__empty-state {
  background: #F8FAFC;
  height: 100%;
  padding: 16px 20px;
  color: #64748B;
}
.business-settings__col--categories-empty {
  height: 100%;
  background: #F8FAFC;
}
.business-settings__form-heading {
  flex: 0 0 auto;
  padding: 20px 24px 0;
  margin-bottom: 16px;
}
.business-settings__form-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.business-settings__form-eyebrow {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9CA3AF;
  margin-bottom: 2px;
}
.business-settings__form-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}
.business-settings__snackbar >>> .v-snack__wrapper {
  background-color: #00BCD4 !important;
  color: #fff !important;
  border-radius: 10px !important;
}
</style>
